import { useEffect, useRef, useState } from 'react';
import { useInfiniteAuctions } from '@/hooks/useAuctions';
import { useFiltersStore } from '@/lib/filtersStore';
import { useDebounce } from '@/hooks/useDebounce';
import { haptic } from '@/lib/telegram';

import { SearchBar } from '@/components/common/SearchBar';
import { TagChips } from '@/components/common/TagChips';
import { SortSheet } from '@/components/common/SortSheet';
import { EmptyState } from '@/components/common/EmptyState';
import { AuctionCard } from '@/components/auction/AuctionCard';
import { AuctionCardSkeleton } from '@/components/auction/AuctionCardSkeleton';
import { Button } from '@/components/ui/Button';

import styles from './FeedPage.module.css';

const SORT_LABELS: Record<string, string> = {
  ending_soon: 'Скоро закончатся',
  newest: 'Новые',
  price_asc: 'Дешевле',
  price_desc: 'Дороже',
  bids_count: 'Больше ставок',
  popular: 'Популярные',
};

export const FeedPage = () => {
  const [sortOpen, setSortOpen] = useState(false);

  const search = useFiltersStore((s) => s.search);
  const selectedTags = useFiltersStore((s) => s.selectedTags);
  const sort = useFiltersStore((s) => s.sort);
  const reset = useFiltersStore((s) => s.reset);

  // Дебаунсим только поисковый запрос (теги/сортировка — мгновенно)
  const debouncedSearch = useDebounce(search, 300);

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteAuctions({
    search: debouncedSearch || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
    sort,
    per_page: 20,
  });

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '300px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const hasActiveFilters = selectedTags.length > 0 || !!debouncedSearch;

  return (
    <div className={styles.page}>
      {/* Sticky шапка */}
      <header className={styles.stickyHeader}>
        <div className={styles.headerTop}>
          <div>
            <p className={styles.eyebrow}>Collector&apos;s Market</p>
            <h1 className={styles.title}>Лента</h1>
          </div>
          <button
            className={styles.sortBtn}
            onClick={() => { haptic.light(); setSortOpen(true); }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M6 12h12M10 18h4" />
            </svg>
            {SORT_LABELS[sort]}
          </button>
        </div>

        <SearchBar />
        <TagChips />
      </header>

      {/* Statusbar: счётчик / индикатор обновления */}
      <div className={styles.statusbar}>
        {isLoading ? (
          <span className={styles.statusText}>Загрузка…</span>
        ) : isError ? (
          <span className={styles.statusError}>Ошибка загрузки</span>
        ) : (
          <span className={styles.statusText}>
            {total} лот{plural(total)}
            {isFetching && !isFetchingNextPage && <span className={styles.updatingDot} />}
          </span>
        )}
      </div>

      {/* Контент */}
      {isError ? (
        <EmptyState
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          }
          title="Не удалось загрузить"
          description="Проверь подключение и попробуй ещё раз"
          action={<Button onClick={() => refetch()}>Повторить</Button>}
        />
      ) : isLoading ? (
        <div className={styles.grid}>
          {Array.from({ length: 6 }).map((_, i) => (
            <AuctionCardSkeleton key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          }
          title="Ничего не нашлось"
          description={
            hasActiveFilters
              ? 'Попробуй убрать часть фильтров'
              : 'Пока тут пусто. Возвращайся позже.'
          }
          action={
            hasActiveFilters ? (
              <Button variant="secondary" onClick={reset}>
                Сбросить фильтры
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className={styles.grid}>
            {items.map((auction, idx) => (
              <AuctionCard key={auction.id} auction={auction} index={idx} />
            ))}
            {isFetchingNextPage &&
              Array.from({ length: 2 }).map((_, i) => (
                <AuctionCardSkeleton key={`more-${i}`} />
              ))}
          </div>
          {hasNextPage && <div ref={loadMoreRef} className={styles.loadMore} aria-hidden />}
        </>
      )}

      <SortSheet isOpen={sortOpen} onClose={() => setSortOpen(false)} />
    </div>
  );
};

const plural = (n: number) => {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'ов';
  if (last === 1) return '';
  if (last >= 2 && last <= 4) return 'а';
  return 'ов';
};